import { useState } from 'react';
import { formatMoney, parseAmount } from '@domain';
import type { CourseInput, TrainingCourse } from '../api.js';
import type { Lang, Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card, PageTitle } from '../components/ui.js';

/**
 * The course catalogue.
 *
 * A course is the thing that gets taught; a delivery is one time it was. They
 * are kept apart because the question a training centre actually asks is
 * "does this course make money" — which can only be answered by comparing the
 * times it ran, and only if the runs know they are runs of the same thing.
 *
 * Set up once and then rarely touched, so the screen is a list you can type
 * in rather than a form behind a button.
 */

const BLANK: CourseInput = {
  courseId: null,
  name: '',
  nameAr: '',
  code: '',
  defaultFee: null,
  active: true,
};

export function CoursesScreen({
  courses,
  currency,
  loading,
  busy,
  error,
  lang,
  canSell,
  t,
  onSave,
  onSellAsProduct,
}: {
  courses: TrainingCourse[];
  currency: string;
  loading: boolean;
  busy: boolean;
  error: string | null;
  lang: Lang;
  /** Writing into the company's product catalogue is an administrator's call. */
  canSell: boolean;
  t: Translate;
  onSave: (course: CourseInput) => void;
  onSellAsProduct: (courseId: number, unlink: boolean) => void;
}) {
  const [editing, setEditing] = useState<CourseInput | null>(null);

  const field = {
    height: '34px',
    width: '100%',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 10px',
    fontSize: '13px',
    background: '#fff',
  };

  const label = { display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' };

  const cell = {
    padding: '10px 12px',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.dividerSoft}`,
  };

  const heading = {
    padding: '9px 12px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '.04em',
    color: theme.label,
    borderBottom: `1px solid ${theme.border}`,
    textAlign: 'start' as const,
  };

  const ready = editing !== null && editing.name.trim() !== '' && !busy;

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '920px' }}>
      <PageTitle
        title={t('Courses')}
        sub={t('What you teach. One entry per course, however many times it runs.')}
      />
      <div style={{ height: '16px' }} />

      {error && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{ padding: '13px 16px', color: theme.badDeep, fontWeight: 600, fontSize: '13px' }}
          >
            {t(error)}
          </div>
        </Card>
      )}

      {editing === null && (
        <button
          type="button"
          onClick={() => setEditing({ ...BLANK })}
          style={{
            background: theme.nav,
            color: '#fff',
            border: 0,
            borderRadius: theme.radiusControl,
            padding: '10px 16px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: '14px',
          }}
        >
          + {t('Add a course')}
        </button>
      )}

      {editing !== null && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${theme.dividerSoft}`,
              fontWeight: 600,
              fontSize: '13px',
              background: theme.surfaceHeader,
            }}
          >
            {editing.courseId === null ? t('Add a course') : t('Edit course')}
          </div>

          <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
              <label style={{ flex: '1 1 260px', minWidth: 0 }}>
                <span style={label}>{t('Course name')}</span>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  style={field}
                  autoFocus
                />
              </label>

              {/* Both languages side by side, not a translation of the other.
                  A centre teaching in Arabic names its courses in Arabic. */}
              <label style={{ flex: '1 1 260px', minWidth: 0 }}>
                <span style={label}>
                  {t('Course name in Arabic')}{' '}
                  <span style={{ fontWeight: 400, color: theme.textMuted }}>({t('optional')})</span>
                </span>
                <input
                  value={editing.nameAr ?? ''}
                  dir="rtl"
                  onChange={(e) => setEditing({ ...editing, nameAr: e.target.value })}
                  style={field}
                />
              </label>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
              <label style={{ flex: '0 1 170px', minWidth: 0 }}>
                <span style={label}>
                  {t('Code')}{' '}
                  <span style={{ fontWeight: 400, color: theme.textMuted }}>({t('optional')})</span>
                </span>
                <input
                  value={editing.code ?? ''}
                  onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                  style={field}
                />
              </label>

              <label style={{ flex: '0 1 200px', minWidth: 0 }}>
                <span style={label}>{t('Usual fee per seat')}</span>
                <input
                  value={editing.defaultFee === null ? '' : String(editing.defaultFee)}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      defaultFee: e.target.value.trim() === '' ? null : parseAmount(e.target.value),
                    })
                  }
                  inputMode="decimal"
                  dir="ltr"
                  style={{ ...field, fontFamily: theme.fontMono }}
                />
              </label>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  alignSelf: 'end',
                  height: '34px',
                  fontSize: '13px',
                }}
              >
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                />
                {t('Still offered')}
              </label>
            </div>

            <div style={{ display: 'flex', gap: '9px' }}>
              <button
                type="button"
                disabled={!ready}
                onClick={() => {
                  onSave(editing);
                  setEditing(null);
                }}
                style={{
                  height: '38px',
                  minWidth: '150px',
                  background: ready ? theme.nav : theme.borderStrong,
                  color: '#fff',
                  border: 0,
                  borderRadius: theme.radiusControl,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: ready ? 'pointer' : 'default',
                }}
              >
                {busy ? t('Saving…') : t('Save')}
              </button>

              <button
                type="button"
                onClick={() => setEditing(null)}
                style={{
                  height: '38px',
                  padding: '0 16px',
                  background: 'transparent',
                  border: `1px solid ${theme.borderStrong}`,
                  borderRadius: theme.radiusControl,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        {loading && (
          <div style={{ padding: '18px', fontSize: '13px', color: theme.textMuted }}>
            {t('Loading…')}
          </div>
        )}

        {!loading && courses.length === 0 && (
          <div style={{ padding: '18px', fontSize: '13px', color: theme.textMuted }}>
            {t('No courses yet. Add the first one above.')}
          </div>
        )}

        {!loading && courses.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={heading}>{t('Course')}</th>
                <th style={heading}>{t('Code')}</th>
                <th style={{ ...heading, textAlign: 'end' }}>{t('Usual fee')}</th>
                <th style={{ ...heading, textAlign: 'end' }}>{t('Times run')}</th>
                <th style={heading}>{t('Product in Xero')}</th>
                <th style={{ ...heading, width: '90px' }} />
              </tr>
            </thead>
            <tbody>
              {courses.map((course) => (
                <tr key={course.courseId} style={{ opacity: course.active ? 1 : 0.55 }}>
                  <td style={cell}>
                    {/* The client's own words for their own course, in the
                        language they are reading. Never run through the
                        phrase dictionary. */}
                    {(lang === 'ar' ? course.nameAr : course.name) || course.name}
                    {!course.active && (
                      <span style={{ color: theme.textMuted, fontSize: '11.5px' }}>
                        {' · '}
                        {t('no longer offered')}
                      </span>
                    )}
                  </td>
                  <td style={{ ...cell, fontFamily: theme.fontMono, fontSize: '12px' }}>
                    {course.code ?? '—'}
                  </td>
                  <td
                    style={{
                      ...cell,
                      textAlign: 'end',
                      fontFamily: theme.fontMono,
                      direction: 'ltr',
                    }}
                  >
                    {course.defaultFee === null
                      ? '—'
                      : `${formatMoney(course.defaultFee)} ${currency}`}
                  </td>
                  <td style={{ ...cell, textAlign: 'end', fontFamily: theme.fontMono }}>
                    {course.deliveries}
                  </td>

                  {/*
                    Optional, and the screen should not nag. Xero needs only
                    a description on an invoice line; selling a course as a
                    product is what makes its revenue break down by course
                    inside Xero rather than only here.
                  */}
                  <td style={cell}>
                    {course.xeroItemCode ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span dir="ltr" style={{ fontFamily: theme.fontMono, fontSize: '12px' }}>
                          {course.xeroItemCode}
                        </span>
                        {canSell && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onSellAsProduct(course.courseId, true)}
                            title={t('The product stays in Xero; only the link goes.')}
                            style={{
                              background: 'transparent',
                              border: 0,
                              color: theme.textMuted,
                              fontSize: '11.5px',
                              fontWeight: 600,
                              cursor: busy ? 'default' : 'pointer',
                              padding: 0,
                            }}
                          >
                            {t('Unlink')}
                          </button>
                        )}
                      </span>
                    ) : canSell ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onSellAsProduct(course.courseId, false)}
                        style={{
                          background: 'transparent',
                          border: `1px dashed ${theme.borderStrong}`,
                          borderRadius: theme.radiusControl,
                          padding: '4px 10px',
                          fontSize: '11.5px',
                          fontWeight: 600,
                          cursor: busy ? 'default' : 'pointer',
                        }}
                      >
                        + {t('Sell as a product')}
                      </button>
                    ) : (
                      <span style={{ color: theme.textFaint, fontSize: '12px' }}>—</span>
                    )}
                  </td>

                  <td style={{ ...cell, textAlign: 'end' }}>
                    <button
                      type="button"
                      onClick={() =>
                        setEditing({
                          courseId: course.courseId,
                          name: course.name,
                          nameAr: course.nameAr ?? '',
                          code: course.code ?? '',
                          defaultFee: course.defaultFee,
                          active: course.active,
                        })
                      }
                      style={{
                        background: 'transparent',
                        border: `1px solid ${theme.border}`,
                        borderRadius: theme.radiusControl,
                        padding: '5px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {t('Edit')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
